/**
 * TanStack Query hooks for the AI training analysis, and the hook that heals a missing or stale one.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { apiFetch } from '../client'
import type { TrainingAnalysisOut } from '../types'
import { OPTIONAL_QUERY_DEFAULTS, noneOn404 } from './shared'
import { useSummary } from './dashboard'

// ── Training Analysis (AI-generated) ────────────────────────────────

/**
 * Fetch the latest AI training analysis, or `null` when none has been generated.
 *
 * The endpoint answers 404 until the first analysis exists. That is an answer,
 * not a failure: delivered as a query error, it made a new user's dashboard read
 * exactly like an outage once the analysis sections learned to say "could not
 * be loaded" (Spec 2 D5, #170). Any other failure is still an error.
 * retry: false prevents spamming retries on a missing analysis.
 */
export function useLatestAnalysis(enabled: boolean = true) {
  return useQuery({
    queryKey: ['analysis', 'latest'],
    queryFn: () => noneOn404(apiFetch<TrainingAnalysisOut>('/api/analysis/latest')),
    ...OPTIONAL_QUERY_DEFAULTS,
    // Off while the AI consent is missing (#399, CS-D14): a stored analysis is not shown then, so it is
    // not fetched either.
    enabled,
  })
}

/** How old the analysis may get before this asks for a new one: **6 days, not 7**,
 *  the server's rule (`ANALYSIS_MAX_AGE` in `api/analysis/generator.py`, which says
 *  why). The hook's tests pin this side, and one of them reads the server's
 *  constant to pin that one. */
const ANALYSIS_MAX_AGE_MS = 6 * 24 * 60 * 60 * 1000

/** `generated_at` as epoch milliseconds, or NaN when it cannot be read.
 *
 *  The API sends the stored UTC wall clock with no offset
 *  ("2026-10-02T10:00:00.123456"), and `Date` reads that form as *local* time, so
 *  the age would be wrong by the viewer's UTC offset: hours, in either direction.
 *  An offset is added when there is none. Fractional seconds are cut to the
 *  milliseconds every engine parses. */
function utcMs(iso: string): number {
  const withOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`
  return Date.parse(withOffset.replace(/(\.\d{3})\d+/, '$1'))
}

/**
 * Self-healing hook: detects missing/incomplete/stale analysis and auto-triggers
 * generation. Call once in a top-level view (e.g. Dashboard).
 *
 * Triggers when:
 *  - No analysis exists (`useLatestAnalysis` resolves the endpoint's 404 to null)
 *  - Analysis exists but key AI fields are null (status_priorities,
 *    summary_strategy) — indicates a schema upgrade or partial generation
 *  - Analysis is 6 days old or older by its own timestamp (the analysis is
 *    weekly, #517)
 *  - Analysis was written when the user had no sessions and the summary has
 *    some now (once per user)
 *
 * **A new session alone is not a trigger** (#517): it used to be, by comparing
 * session counts and dates, and the analysis was rewritten after every session
 * the user followed with a Dashboard visit. The trade is freshness for cost: the
 * Dashboard's analysis can lag a new session by up to 6 days. The coach does not
 * read it.
 *
 * The server is the authority (`check_analysis_stale` in
 * `api/analysis/generator.py`) and this only decides whether to ask, so it
 * agrees with it on the reasons it can see. The server also asks when the
 * analysis was written by an older prompt version, which is not sent to the
 * client, so this cannot ask for that. A request the server skips costs nothing.
 *
 * Guards:
 *  - Only one generation attempt per session (sessionStorage-guarded)
 *  - Nothing at all while `enabled` is false: the AI consent is missing (#399, CS-D14)
 *  - Silently swallows errors — generation is best-effort
 *  - Invalidates the analysis query key on success so all consumers refresh
 */
const HEAL_KEY = 'analysis-heal-pending'

export function useAnalysisAutoHeal(enabled: boolean = true) {
  const queryClient = useQueryClient()
  const { data, isError, isLoading } = useLatestAnalysis(enabled)
  const { data: summary, isLoading: summaryLoading } = useSummary()
  const healingRef = useRef(false)

  useEffect(() => {
    // Without the AI consent (#399, CS-D14) the dashboard stops asking the server for an analysis: the
    // request would only be answered "skipped", and a stored one is not shown.
    if (!enabled || isLoading || summaryLoading || healingRef.current) return
    // Debounce across navigations within the same browser session
    if (sessionStorage.getItem(HEAL_KEY)) return

    // Determine if healing is needed. `useLatestAnalysis` resolves "none
    // generated yet" to null (#170); it used to arrive here as a 404 error.
    const isMissing = data === null
    const isIncomplete =
      !isError &&
      data != null &&
      (data.status_priorities == null || data.summary_strategy == null)
    // Old: the server's age rule, by the analysis's own timestamp. A timestamp
    // that cannot be read is NaN, which compares false: the hook does not ask on
    // a value it cannot judge, and the server's Sunday job still covers it.
    const isOld =
      !isError &&
      data != null &&
      Date.now() - utcMs(data.generated_at) >= ANALYSIS_MAX_AGE_MS
    // Written with no sessions while the summary has some now: the one place a
    // session matters. `latest_session_date` is what records "no sessions" (the
    // server's rule reads the same field), and `total_sessions` is the same
    // unified count the server's check reads, across every source.
    const wasWrittenWithNoSessions =
      !isError &&
      data != null &&
      summary != null &&
      data.latest_session_date == null &&
      summary.total_sessions > 0

    if (!isMissing && !isIncomplete && !isOld && !wasWrittenWithNoSessions) return

    healingRef.current = true
    sessionStorage.setItem(HEAL_KEY, '1')

    apiFetch<{ status: string }>('/api/analysis/generate', { method: 'POST' })
      .then((res) => {
        if (res.status === 'generated') {
          queryClient.invalidateQueries({ queryKey: ['analysis', 'latest'] })
        }
        // Clear flag after success/skip so a future stale state can re-trigger
        if (res.status === 'generated' || res.status === 'skipped') {
          sessionStorage.removeItem(HEAL_KEY)
        }
      })
      .catch(() => {
        // Best-effort — clear flag so retry is possible on next navigation
        sessionStorage.removeItem(HEAL_KEY)
      })
  }, [enabled, data, isError, isLoading, summary, summaryLoading, queryClient])
}
